import { getAvailableParameters } from '../../../api/revit/revitBridge';
import { assessMappingFamilyRequirementsGrpc } from '../../../api/grpc/familyRequirementAssessmentClient';
import type { RevitFamily } from '../../../api/revit/types';
import {
  buildAdditionalCategoryParameterScopeRequirements,
  buildConnectorParameterScopeRequirements,
  buildMappingFamilyRequirementAssessmentInputs,
  type MappingParameterScopeRequirement,
} from '../../mappingParameterScopeRequirements';
import { buildTextParameterTypeHints } from '../../revit/wizardParameterDataTypes';
import {
  areProjectParametersAvailableForBindingAndCategories,
  type AvailableProjectParameterDescriptor,
  normalizeProjectParameterCategories,
} from '../../revit/projectParameterMutationPlan';
import {
  type FamilySnapshotFetchOptions,
  type FamilySnapshotResult,
  fetchFamilySnapshotResult,
} from '../../revit/familySnapshotCache';
import { selectFamilyParameterMutationTargets } from '../../revit/familyParameterTargetSelection';
import type {
  AdditionalCategoriesConfiguration,
  ConnectorBindingConfiguration,
  MenuConfiguration,
  ProcessPropertiesConfiguration,
} from '../../../types';
import {
  areAdditionalCategoryProjectParametersAvailable,
  collectAdditionalCategoryTargets,
  resolveAdditionalCategoryParameterBindingKind,
} from './additionalCategoryPlan';
import {
  availableProjectParameterCache,
  cloneAvailableProjectParameters,
  getAvailableParameterCacheKey,
  normalizeParameterAvailabilityOptions,
} from './availableProjectParameterCache';
import { collectProcessConditionParameters, collectProjectPropertyParameters } from './configuration';
import { areConnectorAdditionOperationsAvailable, buildConnectorAdditionOperations } from './connectorPlan';
import { familyHasParameter, familyMatchesAnyObjectType } from './familyParameterAvailability';
import {
  type AvailableProjectParameterFetchOptions,
  type ParameterAvailabilityCheckOptions,
  type ParameterAvailabilitySummary,
} from './models';
import {
  areProjectParametersAvailable,
  createEquivalentParameterNameMap,
  createParameterNameMap,
  hasAvailableEquivalentParameter,
} from './parameterNameAvailability';

export { areProjectParametersAvailable, getMissingParameterNames } from './parameterNameAvailability';

export async function fetchAvailableProjectParameterNames(
  options?: AvailableProjectParameterFetchOptions,
): Promise<string[]> {
  const parameters = await fetchAvailableProjectParameters(options);
  return Array.from(
    new Set(
      parameters.map((parameter) => parameter.name.trim()).filter(Boolean),
    ),
  );
}

export async function fetchAvailableProjectParameters(
  options?: AvailableProjectParameterFetchOptions,
): Promise<AvailableProjectParameterDescriptor[]> {
  const cacheKey = getAvailableParameterCacheKey(options?.cacheScopeKey);
  if (!options?.force) {
    const cached = availableProjectParameterCache.get(cacheKey);
    if (cached) {
      return cloneAvailableProjectParameters(cached);
    }
  }

  try {
    const result = await getAvailableParameters({
      onProgress: options?.onProgress,
    });
    if (!result.success || !Array.isArray(result.data)) {
      return [];
    }

    const descriptors = result.data
      .map((parameter) => {
        const name = parameter?.name?.trim();
        const parameterType = parameter?.parameterType;
        if (
          !name || (parameterType !== 'Type' && parameterType !== 'Instance')
        ) {
          return null;
        }
        const parameterCategories = normalizeProjectParameterCategories(
          parameter.categories,
        );

        return {
          name,
          label: parameter?.label?.trim() || name,
          parameterType,
          group: parameter?.group?.trim() || '',
          dataType: parameter?.dataType ?? '',
          isProjectBinding: parameter.isProjectBinding,
          ...(parameterCategories.length > 0 ? { categories: parameterCategories } : {}),
          ...(parameter.source ? { source: parameter.source } : {}),
        } satisfies AvailableProjectParameterDescriptor;
      })
      .filter(Boolean) as AvailableProjectParameterDescriptor[];
    availableProjectParameterCache.set(
      cacheKey,
      cloneAvailableProjectParameters(descriptors),
    );
    return descriptors;
  } catch {
    return [];
  }
}

export async function fetchRevitFamiliesSnapshot(
  specificParameterNames?: string[],
  options?: Omit<FamilySnapshotFetchOptions, 'parameterNames'>,
): Promise<RevitFamily[]> {
  const result = await fetchRevitFamiliesSnapshotResult(
    specificParameterNames,
    options,
  );
  return result.families;
}

export async function fetchRevitFamiliesSnapshotResult(
  specificParameterNames?: string[],
  options?: Omit<FamilySnapshotFetchOptions, 'parameterNames'>,
): Promise<FamilySnapshotResult> {
  return fetchFamilySnapshotResult({
    cacheScopeKey: options?.cacheScopeKey,
    parameterNames: specificParameterNames,
    scopedFamilyIds: options?.scopedFamilyIds,
    resolveScopedFamilyIds: options?.resolveScopedFamilyIds,
    onProgress: options?.onProgress,
  });
}

export async function checkWorkflowDefaultParametersAvailability(
  selectedParams: Record<string, boolean>,
  options?: AvailableProjectParameterFetchOptions,
): Promise<boolean> {
  const requestedParameters = Object.entries(selectedParams)
    .filter(([, isSelected]) => isSelected)
    .map(([name]) => name);

  if (requestedParameters.length === 0) {
    return false;
  }

  const availableProjectParameters = await fetchAvailableProjectParameterNames(
    options,
  );
  if (
    areProjectParametersAvailable(
      requestedParameters,
      availableProjectParameters,
    )
  ) {
    return true;
  }

  const families = await fetchRevitFamiliesSnapshot(requestedParameters, {
    cacheScopeKey: options?.cacheScopeKey,
    onProgress: options?.onProgress,
  });
  return families.some((family) =>
    requestedParameters.every((parameterName) => familyHasParameter(family, parameterName))
  );
}

export async function checkConnectorParametersAvailability(
  menuConfig: MenuConfiguration,
  connectorConfig: ConnectorBindingConfiguration,
  options?: ParameterAvailabilityCheckOptions,
): Promise<boolean> {
  const availabilityOptions = normalizeParameterAvailabilityOptions(options);
  const operations = buildConnectorAdditionOperations({
    menuConfig,
    connectorConfig,
  });
  const availableProjectParameters = await fetchAvailableProjectParameters(
    availabilityOptions,
  );

  // Project-scope operations judge from the project parameter catalog alone.
  const projectOperations = operations.filter(
    (operation) => operation.destination !== 'family',
  );
  if (
    !areConnectorAdditionOperationsAvailable(
      projectOperations,
      availableProjectParameters,
    )
  ) {
    return false;
  }

  // Family-scope presence is verdicted by the same backend assessment engine
  // that backs mapping readiness and the write planner: the swept family
  // snapshot conflates project bindings with family-document parameters, so an
  // FE matcher over it can never back a "complies" verdict here. The check
  // fails closed — an RPC error or an unsuccessful response reads as "not
  // available", never as "complies".
  return assessFamilyScopeParameterAvailability(
    buildConnectorParameterScopeRequirements({ menuConfig, connectorConfig }),
  );
}

export async function checkProcessAndProjectParametersAvailability(
  processConfig: ProcessPropertiesConfiguration,
  options?: ParameterAvailabilityCheckOptions,
): Promise<boolean> {
  const availabilityOptions = normalizeParameterAvailabilityOptions(options);
  const availableProjectParameters = await fetchAvailableProjectParameters(
    availabilityOptions,
  );
  return areProcessAndProjectParametersAlreadyAvailable(
    processConfig,
    availableProjectParameters,
  );
}

export async function checkAdditionalCategoryParametersAvailability(
  config: AdditionalCategoriesConfiguration,
  objectTypeParameterName: string,
  options?: ParameterAvailabilityCheckOptions,
): Promise<boolean> {
  const availabilityOptions = normalizeParameterAvailabilityOptions(options);
  if ((config.parameterDestination ?? 'project') === 'project') {
    const availableProjectParameters = await fetchAvailableProjectParameters(
      availabilityOptions,
    );
    return areAdditionalCategoryProjectParametersAvailable(
      config,
      availableProjectParameters,
    );
  }

  // Family-scope presence is verdicted by the same backend assessment engine
  // that backs mapping readiness and the write planner. The broad family
  // snapshot's instance sweep samples placed elements, so project-bound
  // parameters look present on families that never received them; even the
  // definition-only snapshot duplicated the readiness matching logic on the
  // FE. One backend verdict keeps availability, readiness, and planning from
  // drifting apart, and the check fails closed: an RPC error or an
  // unsuccessful response reads as "not available", so the wizard can never
  // auto-confirm setup the planner would still mutate.
  return assessFamilyScopeParameterAvailability(
    buildAdditionalCategoryParameterScopeRequirements({
      additionalCategoriesConfig: config,
      objectTypeParameterName,
    }),
  );
}

async function assessFamilyScopeParameterAvailability(
  requirements: MappingParameterScopeRequirement[],
): Promise<boolean> {
  const inputs = buildMappingFamilyRequirementAssessmentInputs(requirements);
  if (inputs.length === 0) {
    return true;
  }
  try {
    const result = await assessMappingFamilyRequirementsGrpc(inputs);
    const assessments = result.assessments;
    return Boolean(
      result.success &&
      assessments &&
      assessments.length > 0 &&
      assessments.every((assessment) => assessment.satisfied),
    );
  } catch {
    return false;
  }
}

export function areAdditionalCategoryParametersAvailable(
  config: AdditionalCategoriesConfiguration,
  families: RevitFamily[],
  objectTypeParameterName: string,
): boolean {
  const targets = collectAdditionalCategoryTargets(config);
  if (targets.length === 0) {
    return true;
  }

  const bindingKind = resolveAdditionalCategoryParameterBindingKind(config);
  return targets.every((target) => {
    const selection = selectFamilyParameterMutationTargets(
      families,
      target.parameterNames,
      bindingKind,
      (family) => familyMatchesAnyObjectType(
        family,
        target.targetObjectTypes,
        objectTypeParameterName,
        bindingKind,
      ),
      buildTextParameterTypeHints(target.parameterNames),
    );
    return selection.matchingFamilyCount > 0 &&
      selection.targetFamilyIds.length === 0;
  });
}

export function areProcessAndProjectParametersAlreadyAvailable(
  processConfig: ProcessPropertiesConfiguration,
  availableProjectParameters: AvailableProjectParameterDescriptor[],
): boolean {
  const hasProcessParameters = areProjectParametersAvailableForBindingAndCategories(
    collectProcessConditionParameters(processConfig),
    'instance',
    ['Piping Systems'],
    availableProjectParameters,
    buildTextParameterTypeHints(
      collectProcessConditionParameters(processConfig),
    ),
  );
  const hasProjectParameters = areProjectParametersAvailableForBindingAndCategories(
    collectProjectPropertyParameters(processConfig),
    'instance',
    ['Project Information'],
    availableProjectParameters,
    buildTextParameterTypeHints(
      collectProjectPropertyParameters(processConfig),
    ),
  );
  return hasProcessParameters && hasProjectParameters;
}

export function summarizeParameterAvailability(
  configuredParameters: string[],
  availableBefore: string[],
  availableAfter: string[],
): ParameterAvailabilitySummary {
  const requested = createParameterNameMap(configuredParameters);
  const before = createEquivalentParameterNameMap(availableBefore);
  const after = createEquivalentParameterNameMap(availableAfter);

  let availableBeforeCount = 0;
  let availableAfterCount = 0;
  let newlyAvailableCount = 0;

  requested.forEach((parameterName) => {
    const existedBefore = hasAvailableEquivalentParameter(
      before,
      parameterName,
    );
    const existsAfter = hasAvailableEquivalentParameter(after, parameterName);

    if (existedBefore) {
      availableBeforeCount += 1;
    }
    if (existsAfter) {
      availableAfterCount += 1;
    }
    if (!existedBefore && existsAfter) {
      newlyAvailableCount += 1;
    }
  });

  return {
    requestedCount: requested.size,
    availableBeforeCount,
    availableAfterCount,
    newlyAvailableCount,
    missingCount: Math.max(requested.size - availableAfterCount, 0),
  };
}
